import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BookingDialog from "@/components/BookingDialog";
import RewardDialog from "@/components/RewardDialog";
import ConciergeDialog from "@/components/ConciergeDialog";
import WalletPassDialog from "@/components/WalletPassDialog";
import GiftCardDialog from "@/components/GiftCardDialog";
import DuckCrest from "@/components/DuckCrest";

/* ── FARMSCAPE — rolling fields, barn, silo, treeline, animated rice sprouts ── */
const SPROUT_POSITIONS = [22, 48, 68, 92, 118, 168, 196, 224, 252, 282, 308, 332, 358, 386, 410, 432];
const SEED_DOTS = [
  { cx: 14, cy: 168 }, { cx: 38, cy: 173 }, { cx: 60, cy: 167 },
  { cx: 84, cy: 171 }, { cx: 110, cy: 168 }, { cx: 140, cy: 174 },
  { cx: 178, cy: 168 }, { cx: 206, cy: 172 }, { cx: 240, cy: 168 },
  { cx: 268, cy: 174 }, { cx: 296, cy: 167 }, { cx: 324, cy: 173 },
  { cx: 352, cy: 168 }, { cx: 376, cy: 171 }, { cx: 402, cy: 168 },
  { cx: 426, cy: 173 },
];

function Sprout({ cx, delay = 0, baseY = 162 }) {
  return (
    <g
      className="farm-sprout"
      style={{ animationDelay: `${delay}s`, transformOrigin: `${cx}px ${baseY}px` }}
    >
      <line x1={cx} y1={baseY} x2={cx} y2={baseY - 7} stroke="var(--ink)" strokeWidth="0.8" strokeLinecap="round"/>
      <ellipse cx={cx - 2.2} cy={baseY - 6} rx="2.4" ry="1.2" fill="var(--ink)" opacity="0.78" transform={`rotate(-30 ${cx - 2.2} ${baseY - 6})`}/>
      <ellipse cx={cx + 2.2} cy={baseY - 6} rx="2.4" ry="1.2" fill="var(--ink)" opacity="0.78" transform={`rotate(30 ${cx + 2.2} ${baseY - 6})`}/>
    </g>
  );
}

function FarmCanvas({ active, setActive }) {
  return (
    <div className="city-canvas" data-testid="city-canvas">
      <div className="sky-paper" />
      <div className="city-label">FARM TO TABLE · SOHO · BATTERSEA · DIFC</div>
      <div className="farm-wordmark" data-testid="hud-wordmark">
        <span className="hud-wm-strong">DUCK</span><span className="hud-wm-italic">Farm</span><sup className="hud-wm-r">®</sup>
      </div>

      <svg
        className="farm-svg"
        viewBox="0 0 440 176"
        preserveAspectRatio="xMidYMax slice"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Sun / morning light */}
        <circle cx="370" cy="46" r="14" fill="var(--ink)" opacity="0.06"/>
        <circle cx="370" cy="46" r="9" fill="var(--ink)" opacity="0.04"/>

        {/* Distant rolling hills */}
        <path
          d="M0,128 Q40,108 90,118 T180,114 T280,122 T370,112 T440,120 L440,176 L0,176 Z"
          fill="var(--ink-3)"
          opacity="0.32"
        />
        {/* Closer hills with paddy lines */}
        <path
          d="M0,142 Q60,128 130,138 T240,132 T340,140 T440,134 L440,176 L0,176 Z"
          fill="var(--ink-2)"
          opacity="0.42"
        />

        {/* Treeline (small dark trees on hill ridge) */}
        <g fill="var(--ink)" opacity="0.5">
          <ellipse cx="36" cy="132" rx="5" ry="6"/>
          <ellipse cx="46" cy="130" rx="6" ry="7"/>
          <ellipse cx="58" cy="132" rx="5" ry="6"/>
          <ellipse cx="200" cy="125" rx="4" ry="5"/>
          <ellipse cx="210" cy="124" rx="5" ry="6"/>
          <ellipse cx="220" cy="125" rx="4" ry="5"/>
          <ellipse cx="396" cy="128" rx="5" ry="6"/>
          <ellipse cx="406" cy="126" rx="6" ry="7"/>
          <ellipse cx="418" cy="128" rx="5" ry="6"/>
        </g>

        {/* Lone tree (foreground accent) */}
        <g>
          <line x1="320" y1="148" x2="320" y2="138" stroke="var(--ink)" strokeWidth="1.4" strokeLinecap="round"/>
          <ellipse cx="320" cy="132" rx="9" ry="10" fill="var(--ink)" opacity="0.85"/>
        </g>

        {/* Silo */}
        <g>
          <rect x="262" y="118" width="13" height="32" fill="var(--ink)" rx="1"/>
          <ellipse cx="268.5" cy="118" rx="6.5" ry="2.8" fill="var(--ink)"/>
          <line x1="262" y1="128" x2="275" y2="128" stroke="var(--cream)" strokeWidth="0.4" opacity="0.4"/>
          <line x1="262" y1="138" x2="275" y2="138" stroke="var(--cream)" strokeWidth="0.4" opacity="0.4"/>
        </g>

        {/* Barn — the brand red, anchored to the centre-left */}
        <g>
          {/* Roof */}
          <polygon
            points="78,150 78,124 100,112 122,124 122,150"
            fill="var(--red)"
            opacity="0.78"
          />
          {/* Roof darker side */}
          <polygon
            points="100,112 122,124 122,150 100,150"
            fill="var(--red-dim)"
            opacity="0.82"
          />
          {/* Barn door */}
          <rect x="96" y="134" width="8" height="16" fill="var(--ink)"/>
          <line x1="100" y1="134" x2="100" y2="150" stroke="var(--cream)" strokeWidth="0.3" opacity="0.5"/>
          {/* White trim (gable accent) */}
          <line x1="78" y1="124" x2="100" y2="112" stroke="var(--cream2)" strokeWidth="0.6"/>
          <line x1="100" y1="112" x2="122" y2="124" stroke="var(--cream2)" strokeWidth="0.6"/>
          <line x1="78" y1="124" x2="122" y2="124" stroke="var(--cream2)" strokeWidth="0.4" opacity="0.6"/>
          {/* Hayloft window */}
          <rect x="98" y="120" width="4" height="4" fill="var(--cream2)" opacity="0.7"/>
        </g>

        {/* Small farmhouse (right side) */}
        <g opacity="0.85">
          <polygon
            points="350,148 350,134 362,126 374,134 374,148"
            fill="var(--ink)"
          />
          <rect x="358" y="140" width="4" height="8" fill="var(--cream2)" opacity="0.6"/>
          <rect x="367" y="138" width="3" height="3" fill="var(--cream2)" opacity="0.6"/>
        </g>

        {/* Fence posts (foreground left) */}
        <g stroke="var(--ink)" strokeWidth="0.6" opacity="0.55">
          <line x1="6" y1="156" x2="6" y2="148"/>
          <line x1="14" y1="156" x2="14" y2="148"/>
          <line x1="22" y1="156" x2="22" y2="148"/>
          <line x1="30" y1="156" x2="30" y2="148"/>
          <line x1="2" y1="152" x2="34" y2="152"/>
        </g>

        {/* Crop furrow lines (perspective rows) */}
        <g stroke="var(--ink)" strokeWidth="0.5" fill="none" opacity="0.18">
          <path d="M0,158 Q220,156 440,158"/>
          <path d="M0,164 Q220,162 440,164"/>
          <path d="M0,170 Q220,168 440,170"/>
        </g>

        {/* Scattered seed dots in the soil */}
        <g fill="var(--ink)" opacity="0.45">
          {SEED_DOTS.map((s, i) => (
            <ellipse key={i} cx={s.cx} cy={s.cy} rx="1.1" ry="0.7"/>
          ))}
        </g>

        {/* Animated rice sprouts growing */}
        <g>
          {SPROUT_POSITIONS.map((cx, i) => (
            <Sprout key={i} cx={cx} delay={(i * 0.42) % 5} />
          ))}
        </g>

        {/* Subtle ground band */}
        <rect x="0" y="151" width="440" height="1" fill="var(--ink)" opacity="0.3"/>
      </svg>

      <div className="pavement">
        <div
          data-testid="district-soho-btn"
          className={`street-tag ${active === "soho" ? "active" : ""}`}
          onClick={() => setActive("soho")}
        >
          <div className="street-dot" />
          SOHO W1 — OPEN
        </div>
        <div
          data-testid="district-difc-btn"
          className={`street-tag ${active === "difc" ? "active" : ""}`}
          onClick={() => setActive("difc")}
        >
          DIFC ↗ 2026
          <div className="street-dot" style={{ background: active === "difc" ? "var(--ink)" : "rgba(0,0,0,0.3)" }} />
        </div>
      </div>
    </div>
  );
}

/* ── MAP TAB ── */
function MapTab({ openBooking }) {
  return (
    <div className="section scroll-inner" data-testid="map-tab">
      <p className="section-title fu">The Club</p>
      <p className="section-sub fu">SOHO · BATTERSEA · DIFC · WORLDWIDE</p>
      <div className="rule fu" />

      <div className="linner-banner fu fu2" onClick={() => openBooking("linner")} data-testid="linner-banner-card" style={{ cursor: "pointer" }}>
        <div className="linner-title">Mahjong Linner Club</div>
        <div className="linner-body">
          Saturday & Sunday, 15:00–18:00<br/>
          Dim sum · Tank pilsner · Mahjong sets<br/>
          Earn 50 tiles per Linner visit
        </div>
      </div>

      <p className="sub-head fu fu3">YOUR WORLD</p>
      {[
        { e: "🏙", n: "Soho, W1", s: "Open daily · 12:00–01:00", live: true },
        { e: "⚡", n: "Battersea Power Station", s: "Open daily · 12:00–23:30", live: true },
        { e: "🌇", n: "DIFC, Dubai", s: "Opening 2026", live: false },
      ].map((l, i) => (
        <div key={i} className="earn-row" data-testid={`venue-row-${i}`}>
          <div className="earn-left">
            <div className="earn-icon-box">{l.e}</div>
            <div>
              <div className="earn-name">{l.n}</div>
              <div className="earn-hint">{l.s}</div>
            </div>
          </div>
          <div style={{ fontFamily: "'LatinCTSemiCond', 'Fraunces', Georgia, serif", fontSize: 7, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
            {l.live
              ? <span style={{ color: "#1a8a3a" }}>● OPEN</span>
              : <span style={{ color: "var(--gold)" }}>◌ SOON</span>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── CLUB TAB ── */
function ClubTab({ user, openWallet }) {
  const tiers = ["GUEST", "REGULAR", "MEMBER", "CLUB", "SOVEREIGN"];
  const TIER_THRESH = { GUEST: 0, REGULAR: 100, MEMBER: 300, CLUB: 600, SOVEREIGN: 1500 };
  const currentIdx = tiers.indexOf(user?.tier || "GUEST");
  const nextThresh = user?.next_tier?.threshold || 1500;
  const prevThresh = TIER_THRESH[user?.tier || "GUEST"] || 0;
  const pct = user?.next_tier
    ? Math.min(100, Math.max(0, ((user.tiles - prevThresh) / (nextThresh - prevThresh)) * 100))
    : 100;

  const perks = [
    ["Priority booking — 48h ahead", currentIdx >= 2],
    ["Mahjong Linner Club access", currentIdx >= 1],
    ["Monthly Dim Sum credit (200 tiles)", currentIdx >= 2],
    ["Cross-location recognition", currentIdx >= 3],
    ["Sovereign: annual private dinner", currentIdx >= 4],
  ];

  return (
    <div className="section scroll-inner" data-testid="club-tab">
      <p className="section-title fu">Club Card</p>
      <p className="section-sub fu">YOUR MEMBERSHIP</p>
      <div className="rule fu" />

      <div className="club-card fu fu2" data-testid="club-card" onClick={openWallet} role="button" tabIndex={0}>
        <div className="card-hatch" />
        <div className="card-body">
          <div className="card-top">
            <div className="duck-logo duck-logo--gold" style={{ width: 28, height: 28 }} />
            <div className="card-tier">◈ {user?.tier || "GUEST"}</div>
          </div>
          <div className="card-crest">
            <div className="duck-logo duck-logo--gold" style={{ width: 88, height: 88 }} />
          </div>
          <div className="card-bottom">
            <div>
              <div className="card-name">{user?.name || "The Club"}</div>
              <div className="card-num">{user?.member_no}</div>
            </div>
            <div className="card-locs">SOHO W1<br/>BATTERSEA<br/>DIFC ↗</div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="dr-btn-primary wallet-cta fu fu2"
        onClick={openWallet}
        data-testid="open-wallet-btn"
      >
         ADD TO APPLE WALLET
      </button>

      <div className="tier-wrap fu fu3">
        <div className="tier-row">
          {tiers.map((t, i) => (
            <span key={t} className={`tier-label ${i === currentIdx ? "active" : ""}`}>{t}</span>
          ))}
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="tier-next">
          {user?.next_tier
            ? <><span>{user.next_tier.remaining} TILES</span> UNTIL {user.next_tier.name}</>
            : <span>SOVEREIGN — TOP TIER</span>
          }
        </div>
      </div>

      <p className="sub-head fu fu4">MEMBER PRIVILEGES</p>
      <div className="perks-list">
        {perks.map(([p, a], i) => (
          <div key={i} className="perk-row" data-testid={`perk-row-${i}`}>
            <div className="earn-left">
              <span className="status-dot" style={{ background: a ? "#1a8a3a" : "rgba(0,0,0,0.15)" }} />
              <span className="perk-text" style={{ color: a ? "var(--black)" : "rgba(0,0,0,0.35)" }}>{p}</span>
            </div>
            {!a && <span className="perk-lock">UPGRADE</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── TILES TAB ── */
const TILES_DATA = [
  { sym: "🀇", name: "ONE BAMBOO", min: 0 },
  { sym: "🀈", name: "TWO BAMBOO", min: 50 },
  { sym: "🀙", name: "ONE CIRCLE", min: 200 },
  { sym: "🀄", name: "RED DRAGON", min: 300 },
  { sym: "🀅", name: "GRN DRAGON", min: 500 },
  { sym: "🀆", name: "WHT DRAGON", min: 800 },
  { sym: "🀌", name: "FIVE CHAR", min: 1100 },
  { sym: "🀀", name: "EAST WIND", min: 1500 },
];

const EARNS = [
  { i: "🍜", n: "Dine at Duck & Rice", h: "1 tile per £1 spent", v: "+1 / £1", action: "dine" },
  { i: "🀄", n: "Mahjong Linner Club", h: "Sat & Sun 15:00–18:00", v: "+50 tiles", action: "linner" },
  { i: "🎁", n: "Refer a Friend", h: "Both members rewarded", v: "+100 tiles", action: "refer" },
  { i: "🥂", n: "Private Dining", h: "Any event room booking", v: "+200 tiles", action: "private_dining" },
  { i: "📅", n: "Birthday Month", h: "Automatic annual bonus", v: "+250 tiles", action: "birthday" },
];

function TilesTab({ user, onEarn, onCheckin }) {
  return (
    <div className="section scroll-inner" data-testid="tiles-tab">
      <p className="section-title fu">Your Tiles</p>
      <p className="section-sub fu">{user?.tiles ?? 0} TILES · {user?.tier ?? "GUEST"} TIER</p>
      <div className="rule fu" />

      <button className="checkin-btn fu fu2" onClick={onCheckin} data-testid="daily-checkin-btn">
        ◈ DAILY CHECK-IN — +10 TILES
      </button>

      <div className="tiles-grid fu fu2">
        {TILES_DATA.map((t, i) => {
          const owned = (user?.tiles ?? 0) >= t.min;
          return (
            <div key={i} className={`tile-card ${owned ? "owned" : ""}`} data-testid={`tile-${i}`}>
              <div className="tile-sym">{t.sym}</div>
              <div className="tile-nm">{t.name}</div>
            </div>
          );
        })}
      </div>

      <p className="sub-head fu fu3">WAYS TO EARN</p>
      {EARNS.map((e, i) => (
        <div key={i} className="earn-row fu" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`earn-row-${e.action}`}>
          <div className="earn-left">
            <div className="earn-icon-box">{e.i}</div>
            <div>
              <div className="earn-name">{e.n}</div>
              <div className="earn-hint">{e.h}</div>
            </div>
          </div>
          <div className="earn-badge">{e.v}</div>
        </div>
      ))}
    </div>
  );
}

/* ── BOOK TAB ── */
const BOOKS = [
  { type: "table", i: "🍜", n: "Table Reservation", d: "Soho · Battersea · DIFC — Lunch & Dinner", locs: ["SOHO W1", "BATTERSEA", "DIFC"], feat: false },
  { type: "linner", i: "🀄", n: "Mahjong Linner Club", d: "Sat & Sun 15:00–18:00\nDim sum, tank pilsner & mahjong sets", locs: ["SOHO W1", "BATTERSEA"], feat: true },
  { type: "private_dining", i: "🥂", n: "Private Dining", d: "Groups from 10 · Bespoke menus", locs: ["SOHO W1", "BATTERSEA", "DIFC"], feat: false },
  { type: "event", i: "🎉", n: "Events & Experiences", d: "CNY · Moon Festival · Tastings", locs: ["SOHO W1", "BATTERSEA"], feat: false },
];

function BookTab({ openBooking, bookings, onCancel }) {
  return (
    <div className="section scroll-inner" data-testid="book-tab">
      <p className="section-title fu">Reserve</p>
      <p className="section-sub fu">BOOK YOUR VISIT</p>
      <div className="rule fu" />

      {BOOKS.map((b, i) => (
        <div
          key={i}
          className={`book-card ${b.feat ? "featured" : ""} fu`}
          style={{ animationDelay: `${i * 0.07}s` }}
          onClick={() => openBooking(b.type)}
          data-testid={`book-card-${b.type}`}
        >
          {b.feat && <div className="linner-badge">LINNER CLUB</div>}
          <div className="book-icon">{b.i}</div>
          <div style={{ flex: 1 }}>
            <div className="book-title">{b.n}</div>
            <div className="book-detail">{b.d}</div>
            <div className="book-locs">
              {b.locs.map(l => <span key={l} className="loc-tag">{l}</span>)}
            </div>
          </div>
        </div>
      ))}

      {bookings && bookings.length > 0 && (
        <>
          <p className="sub-head fu" style={{ marginTop: 24 }}>UPCOMING & PAST</p>
          {bookings.slice(0, 5).map(b => {
            const cancellable = b.status !== "cancelled" && b.status !== "pending_deposit";
            return (
              <div key={b.id} className="booking-history-row" data-testid={`booking-row-${b.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="booking-type">{b.booking_type.replace("_", " ").toUpperCase()}</div>
                  <div className="booking-meta">{b.date} · {b.time} · {b.party_size}p · {b.venue.toUpperCase()}</div>
                </div>
                <div className="booking-status-col">
                  <div className={`booking-status booking-status--${b.status}`}>{b.status.replace("_", " ")}</div>
                  {cancellable && (
                    <button
                      type="button"
                      className="booking-cancel-link"
                      onClick={() => onCancel(b)}
                      data-testid={`cancel-booking-${b.id}`}
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

/* ── REWARDS TAB ── */
function RewardsTab({ rewards, openRedeem, openGift }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const redeemCode = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/redeem", { code: c });
      toast.success(`+${data.tiles_credited} tiles · gift from ${data.sender_name}`);
      setCode("");
      // Trigger profile refresh on parent via custom event
      window.dispatchEvent(new CustomEvent("dr-refresh-profile"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't redeem");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="section scroll-inner" data-testid="rewards-tab">
      <p className="section-title fu">Redeem</p>
      <p className="section-sub fu">SPEND YOUR TILES</p>
      <div className="rule fu" />

      <div className="gift-cta-row fu" data-testid="gift-cta-row">
        <button
          type="button"
          className="gift-cta-btn"
          onClick={openGift}
          data-testid="open-gift-btn"
        >
          <span className="gift-cta-icon">✦</span>
          <div className="gift-cta-body">
            <div className="gift-cta-title">SEND A GIFT</div>
            <div className="gift-cta-sub">Digital pass · from £25</div>
          </div>
          <span className="gift-cta-arrow">→</span>
        </button>

        <form onSubmit={redeemCode} className="gift-redeem-row">
          <input
            data-testid="gift-redeem-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="DR-GIFT-XXXXXXXX"
            maxLength={20}
          />
          <button
            type="submit"
            className="dr-btn-primary gift-redeem-btn"
            disabled={busy || !code.trim()}
            data-testid="gift-redeem-btn"
          >
            {busy ? "…" : "REDEEM"}
          </button>
        </form>
      </div>

      {rewards.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", fontFamily: "'LatinCTSemiCond', 'Fraunces', Georgia, serif", fontSize: 8, letterSpacing: "0.2em" }}>
          LOADING REWARDS…
        </div>
      )}

      {rewards.map((r, i) => (
        <div
          key={r.id}
          className={`reward-card ${r.locked ? "locked" : ""} ${!r.affordable && !r.locked ? "unaffordable" : ""} fu`}
          style={{ animationDelay: `${i * 0.06}s` }}
          onClick={() => !r.locked && r.affordable && openRedeem(r)}
          data-testid={`reward-card-${r.id}`}
        >
          <div className="reward-top">
            <div className="reward-name">{r.name}</div>
            <div className="reward-cost">🀄 {r.cost}</div>
          </div>
          <div className="reward-desc">{r.description}</div>
          <div className="reward-tags">
            {r.venue_tag === "soho" && <span className="tag tag-soho">SOHO ONLY</span>}
            {r.venue_tag === "difc" && <span className="tag tag-difc">DIFC ONLY</span>}
            {r.venue_tag === "both" && <span className="tag tag-both">ALL VENUES</span>}
            {r.limited && <span className="tag tag-ltd">LIMITED</span>}
            {r.locked && <span className="tag tag-difc">{r.min_tier}+</span>}
            {!r.affordable && !r.locked && <span className="tag tag-soho">NEED MORE TILES</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── ROOT ── */
export default function DuckRiceApp() {
  const { user, setUser, refresh, logout } = useAuth();
  const [tab, setTab] = useState("map");
  const [district, setDistrict] = useState("soho");
  const [rewards, setRewards] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingDialog, setBookingDialog] = useState({ open: false, type: null });
  const [rewardDialog, setRewardDialog] = useState({ open: false, reward: null });
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [profileRes, rewardsRes, bookingsRes] = await Promise.all([
        api.get("/profile"),
        api.get("/rewards"),
        api.get("/bookings"),
      ]);
      setUser(profileRes.data);
      setRewards(rewardsRes.data);
      setBookings(bookingsRes.data);
    } catch (e) {
      // silent — auth may be expired
    }
  }, [setUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* External refresh trigger (e.g. after gift redeem from RewardsTab) */
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener("dr-refresh-profile", handler);
    return () => window.removeEventListener("dr-refresh-profile", handler);
  }, [loadAll]);

  /* ── Stripe return-from-checkout: poll status, confirm booking, award tiles ── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          if (data.user) setUser(data.user);
          if (data.purpose === "gift_card" && data.gift) {
            const link = `${window.location.origin}/gift/${data.gift.code}`;
            try { await navigator.clipboard.writeText(link); } catch {}
            toast.success(`Gift activated · ${data.gift.code} (link copied)`, { duration: 6000 });
            setTab("rewards");
          } else {
            toast.success(`Deposit received · +${data.tiles_awarded || 0} tiles`);
            setTab("book");
          }
          sessionStorage.removeItem("dr_pending_session");
          sessionStorage.removeItem("dr_pending_gift_code");
          window.history.replaceState({}, "", window.location.pathname);
          loadAll();
          return;
        }
        if (data.status === "expired" || ["canceled", "expired"].includes(data.payment_status)) {
          toast.error("Payment was not completed");
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
      } catch (e) {
        // network blip — keep polling
      }
      if (attempts < 8) setTimeout(poll, 2000);
      else {
        toast.message("Still confirming your payment — refresh in a moment.");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [loadAll, setUser]);

  /* ── Auto-redeem any gift the user landed on /gift/<code> for, after they signed in ── */
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem("dr_pending_gift_claim");
    if (!pending) return;
    sessionStorage.removeItem("dr_pending_gift_claim");
    api.post("/gifts/redeem", { code: pending })
      .then(({ data }) => {
        setUser(data.user);
        toast.success(`+${data.tiles_credited} tiles · gift from ${data.sender_name}`);
        loadAll();
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't redeem gift"));
  }, [user, loadAll, setUser]);

  const onCheckin = async () => {
    try {
      const { data } = await api.post("/tiles/earn", { action_type: "checkin" });
      setUser({ ...user, ...data.user });
      toast.success(`+${data.awarded} tiles · ${data.label}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Check-in failed");
    }
  };

  const openBooking = (type) => setBookingDialog({ open: true, type });
  const openRedeem = (reward) => setRewardDialog({ open: true, reward });

  const handleBookingDone = (data) => {
    setUser({ ...user, ...data.user });
    setBookings([data.booking, ...bookings]);
    toast.success(`Booked · +${data.tiles_awarded} tiles`);
    setBookingDialog({ open: false, type: null });
    loadAll();
  };

  const handleRedeemDone = (data) => {
    setUser({ ...user, ...data.user });
    toast.success(`Redeemed · code ${data.redemption.code}`);
    setRewardDialog({ open: false, reward: null });
    loadAll();
  };

  const handleCancelBooking = async (b) => {
    if (!window.confirm(`Cancel ${b.booking_type.replace("_", " ")} on ${b.date} at ${b.time}?`)) return;
    try {
      const { data } = await api.delete(`/bookings/${b.id}`);
      if (data.user) setUser(data.user);
      const refundMsg = data.refund?.issued
        ? "Deposit refund initiated"
        : (data.refund?.reason || (b.deposit_paid ? "No refund issued" : "Cancelled"));
      const tileMsg = data.tiles_clawback ? ` · −${data.tiles_clawback} tiles` : "";
      toast.success(`${refundMsg}${tileMsg}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't cancel");
    }
  };

  const TABS = [
    { id: "map",     icon: "🗺",  label: "WORLD"  },
    { id: "club",    icon: "🃏",  label: "CLUB"   },
    { id: "tiles",   icon: "🀄",  label: "TILES"  },
    { id: "book",    icon: "📅",  label: "BOOK"   },
    { id: "rewards", icon: "🎁",  label: "REDEEM" },
  ];

  return (
    <div className="app" data-testid="duckrice-app">
      <FarmCanvas active={district} setActive={setDistrict} />

      <div className="hud" data-testid="hud">
        <div className="hud-brand">
          <div
            className="duck-logo duck-logo--gold hud-duck"
            onClick={logout}
            title="Sign out"
            data-testid="logout-btn"
            role="button"
            aria-label="Sign out"
          />
        </div>
        <div className="tile-balance" data-testid="tile-balance">
          <span style={{ fontSize: 13 }}>🀄</span>
          <span className="tile-count">{user?.tiles ?? 0} TILES</span>
        </div>
      </div>

      <div className="content" data-testid="content">
        {tab === "map"     && <MapTab openBooking={openBooking} />}
        {tab === "club"    && <ClubTab user={user} openWallet={() => setWalletOpen(true)} />}
        {tab === "tiles"   && <TilesTab user={user} onCheckin={onCheckin} />}
        {tab === "book"    && <BookTab openBooking={openBooking} bookings={bookings} onCancel={handleCancelBooking} />}
        {tab === "rewards" && <RewardsTab rewards={rewards} openRedeem={openRedeem} openGift={() => setGiftOpen(true)} />}
      </div>

      <button
        className="concierge-fab"
        onClick={() => setConciergeOpen(true)}
        data-testid="concierge-fab"
        aria-label="Open concierge"
      >
        ✦
      </button>

      <nav className="nav" data-testid="bottom-nav">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`nav-${t.id}`}
            className={`nav-btn ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <span className="nav-icon">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </nav>

      <BookingDialog
        open={bookingDialog.open}
        type={bookingDialog.type}
        onClose={() => setBookingDialog({ open: false, type: null })}
        onDone={handleBookingDone}
      />
      <RewardDialog
        open={rewardDialog.open}
        reward={rewardDialog.reward}
        onClose={() => setRewardDialog({ open: false, reward: null })}
        onDone={handleRedeemDone}
      />
      <ConciergeDialog
        open={conciergeOpen}
        onClose={() => setConciergeOpen(false)}
      />
      <WalletPassDialog
        open={walletOpen}
        user={user}
        onClose={() => setWalletOpen(false)}
      />
      <GiftCardDialog
        open={giftOpen}
        onClose={() => setGiftOpen(false)}
      />
    </div>
  );
}
